/**
 * Parent/host side of the bridge. Listens for typed requests from embedded
 * iframes, dispatches them to registered handlers, and posts the response
 * back to `event.source` on the iframe's origin.
 *
 * The Shell owns business logic (e.g. calling Shopify Ajax cart); this
 * module owns the wire format.
 */

import {
  type BridgeEnvelope,
  BridgeEnvelopeType,
  BridgeEnvelopeVersion,
  type BridgeRequest,
  type BridgeRequestKind,
  type BridgeResponse,
  type BridgeResponseBodyOf,
  isBridgeRequestEnvelope,
} from './messages'

export type HostHandlerContext = {
  source: MessageEventSource
  origin: string
}

export type HostHandler<K extends BridgeRequestKind> = (
  request: Extract<BridgeRequest, {kind: K}>,
  ctx: HostHandlerContext
) => Promise<BridgeResponseBodyOf<K>>

export type HostHandlers = {
  [K in BridgeRequestKind]: HostHandler<K>
}

export type CreateHostBridgeOptions = {
  /** The window listening for requests (usually the parent document). */
  window: Window
  /** Origins we trust to send requests (the studio iframe origin). */
  allowedOrigins: string[]
  /** One handler per request kind. */
  handlers: HostHandlers
  /**
   * Origin to target when posting responses back. If omitted, the response is
   * sent to the first allowed origin (or `event.origin` if the request came
   * from an allowed source). Use this to pin responses to the studio origin.
   */
  responseTargetOrigin?: string
}

export type HostBridge = {destroy(): void}

export function createHostBridge(options: CreateHostBridgeOptions): HostBridge {
  const {window: win, allowedOrigins, handlers} = options

  const onMessage = (event: MessageEvent) => {
    if (!allowedOrigins.includes(event.origin)) return
    if (!event.source || !('postMessage' in event.source)) return
    if (!isBridgeRequestEnvelope(event.data)) return

    const envelope = event.data
    const handler = handlers[envelope.payload.kind] as HostHandler<typeof envelope.payload.kind>
    if (!handler) return

    const source: MessageEventSource = event.source
    const targetOrigin = options.responseTargetOrigin ?? event.origin

    void Promise.resolve()
      .then(() =>
        handler(envelope.payload as Extract<BridgeRequest, {kind: typeof envelope.payload.kind}>, {
          source,
          origin: event.origin,
        })
      )
      .then((body) => {
        const response: BridgeEnvelope<BridgeResponse> = {
          v: BridgeEnvelopeVersion,
          type: BridgeEnvelopeType,
          direction: 'response',
          requestId: envelope.requestId,
          payload: {kind: envelope.payload.kind, ...body} as BridgeResponse,
        }
        ;(event.source as WindowProxy).postMessage(response, targetOrigin)
      })
      .catch(() => {
        // Handlers are expected to return error shapes in their own payloads
        // (e.g. `success: false, errorType: ...`). If one throws, surface a
        // generic rejection so the iframe's timeout still clears quickly.
        const response: BridgeEnvelope<BridgeResponse> = {
          v: BridgeEnvelopeVersion,
          type: BridgeEnvelopeType,
          direction: 'response',
          requestId: envelope.requestId,
          payload: {kind: envelope.payload.kind, success: false, errorType: 'rejected'} as BridgeResponse,
        }
        ;(event.source as WindowProxy).postMessage(response, targetOrigin)
      })
  }

  win.addEventListener('message', onMessage)

  return {
    destroy() {
      win.removeEventListener('message', onMessage)
    },
  }
}
